import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { api } from '../lib/api';
import { money, odds } from '../lib/format';

/*
 * The bet slip: tap a price anywhere, check the stake, add it to My Bets.
 * One slip for the whole app, like a sportsbook's. Selections survive a
 * reload (localStorage); stakes start at the model's suggestion or one unit.
 *
 * The toast is here too: every "added" message comes with Undo or View.
 */

export type SlipPick = {
  /** One selection per market side per game: `${gameId}:${market}:${side}`. */
  key: string;
  gameId: string;
  season?: number;
  week: number;
  matchup: string;
  market: 'spread' | 'total' | 'moneyline' | 'prop';
  side: 'home' | 'away' | 'over' | 'under';
  /** "BUF -7", "Over 48.5", "NE ML" */
  selection: string;
  /** From the side that was bet: -7 for BUF -7, 48.5 for a total. */
  line: number | null;
  price: number;
  /** The model's suggested stake when it likes this side. */
  suggestedStake?: number;
  modelProb?: number;
  modelEv?: number;
  book?: string | null;
  /** True when this is the model's side. */
  modelPick?: boolean;
  /** Player props: who and which stat, so the bet can be graded. */
  playerId?: string;
  stat?: string;
  /** The book posted a line but no price; the slip assumes -110 and says so. */
  priceEstimated?: boolean;
};

/** The two sides of one bet share a group: picking one replaces the other. */
const groupOf = (p: SlipPick) =>
  p.market === 'prop' ? `${p.gameId}:prop:${p.playerId}:${p.stat}` : `${p.gameId}:${p.market}`;

type Toast = { id: number; text: string; action?: { label: string; run: () => void | Promise<void> }; link?: { label: string; to: string } };

type Slip = {
  picks: SlipPick[];
  has: (key: string) => boolean;
  /** Adds the pick, or takes it off the slip if it is already there. */
  toggle: (pick: SlipPick) => void;
  remove: (key: string) => void;
  clear: () => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  notify: (toast: Omit<Toast, 'id'>) => void;
  unitSize: number;
};

const MARKET_NAME: Record<SlipPick['market'], string> = {
  spread: 'Spread', total: 'Total', moneyline: 'Moneyline', prop: 'Player prop',
};

const SlipContext = createContext<Slip | null>(null);

export function useBetSlip(): Slip {
  const ctx = useContext(SlipContext);
  if (!ctx) throw new Error('useBetSlip outside BetSlipProvider');
  return ctx;
}

const STORE = 'betslip.picks';

function load(): SlipPick[] {
  try {
    const raw = localStorage.getItem(STORE);
    return raw ? (JSON.parse(raw) as SlipPick[]) : [];
  } catch {
    return [];
  }
}

/** What a winning stake returns in profit at American odds. */
export function toWin(stake: number, price: number): number {
  if (!Number.isFinite(stake) || stake <= 0) return 0;
  return price > 0 ? (stake * price) / 100 : (stake * 100) / Math.abs(price);
}

export function BetSlipProvider({ children }: { children: ReactNode }) {
  const [picks, setPicks] = useState<SlipPick[]>(load);
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [unitSize, setUnitSize] = useState(10);
  const fetched = useRef(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORE, JSON.stringify(picks));
    } catch {
      /* private mode: the slip just won't survive a reload */
    }
  }, [picks]);

  // The default stake is the user's unit; only ask once there is a slip.
  useEffect(() => {
    if (!picks.length || fetched.current) return;
    fetched.current = true;
    api.settings().then((s) => setUnitSize(s.bankroll.unitSize)).catch(() => {});
  }, [picks.length]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast((cur) => (cur?.id === toast.id ? null : cur)), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const toggle = useCallback((pick: SlipPick) => {
    setPicks((cur) => {
      if (cur.some((p) => p.key === pick.key)) return cur.filter((p) => p.key !== pick.key);
      // One side per market per game: picking BUF -7 replaces NE +7, and
      // a player's over replaces his under.
      return [...cur.filter((p) => groupOf(p) !== groupOf(pick)), pick];
    });
    // Open the slip, unless a panel (a player, a game) is open: then the
    // floating button counts up instead of covering what they're reading.
    if (!document.querySelector('[role="dialog"][aria-modal="true"]')) setOpen(true);
  }, []);

  const value = useMemo<Slip>(
    () => ({
      picks,
      has: (key) => picks.some((p) => p.key === key),
      toggle,
      remove: (key) => setPicks((cur) => cur.filter((p) => p.key !== key)),
      clear: () => setPicks([]),
      open,
      setOpen,
      notify: (t) => setToast({ ...t, id: Date.now() }),
      unitSize,
    }),
    [picks, toggle, open, unitSize],
  );

  return (
    <SlipContext.Provider value={value}>
      {children}
      <BetSlipPanel />
      {toast && (
        <div className="toast" role="status" aria-live="polite" data-layout-ignore>
          <span className="toast-text">{toast.text}</span>
          {toast.action && (
            <button
              type="button"
              className="toast-action"
              onClick={async () => {
                setToast(null);
                await toast.action!.run();
              }}
            >
              {toast.action.label}
            </button>
          )}
          {toast.link && (
            <Link className="toast-action" to={toast.link.to} onClick={() => setToast(null)}>
              {toast.link.label}
            </Link>
          )}
          <button type="button" className="toast-close" aria-label="Dismiss" onClick={() => setToast(null)}>
            <X size={14} />
          </button>
        </div>
      )}
    </SlipContext.Provider>
  );
}

function BetSlipPanel() {
  const { picks, remove, clear, open, setOpen, notify, unitSize } = useBetSlip();
  const [stakes, setStakes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showing = picks.length > 0 && open;
  useEffect(() => {
    document.body.classList.toggle('slip-open', showing);
    return () => document.body.classList.remove('slip-open');
  }, [showing]);

  if (!picks.length) return null;

  const stakeOf = (p: SlipPick) => {
    const typed = stakes[p.key];
    if (typed != null) return Number(typed);
    return p.suggestedStake && p.suggestedStake > 0 ? p.suggestedStake : unitSize;
  };
  const total = picks.reduce((s, p) => s + (stakeOf(p) || 0), 0);
  const payout = picks.reduce((s, p) => s + toWin(stakeOf(p), p.price), 0);
  const valid = picks.every((p) => stakeOf(p) > 0);

  async function place() {
    setBusy(true);
    setError(null);
    const done: string[] = [];
    try {
      for (const p of picks) {
        await api.createBet({
          season: p.season,
          week: p.week,
          gameId: p.gameId,
          matchup: p.matchup,
          market: p.market,
          selection: p.selection,
          side: p.side,
          line: p.line,
          price: p.price,
          stake: stakeOf(p),
          book: p.book ?? null,
          modelProb: p.modelProb ?? null,
          modelEv: p.modelEv ?? null,
          playerId: p.playerId,
          stat: p.stat,
        });
        done.push(p.key);
      }
      const n = done.length;
      clear();
      setStakes({});
      setOpen(false);
      notify({ text: `${n} ${n === 1 ? 'bet' : 'bets'} added to My Bets · ${money(total)}`, link: { label: 'View', to: '/ledger' } });
    } catch (err) {
      // Keep what failed on the slip; drop what was saved.
      done.forEach(remove);
      setError((err as Error).message || 'Could not add those bets');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" data-layout-ignore className="slip-fab" onClick={() => setOpen(true)} aria-label={`Open bet slip, ${picks.length} selected`}>
        <span className="slip-fab-count num">{picks.length}</span>
        Bet slip
        <span className="num slip-fab-total">{money(total)}</span>
      </button>
    );
  }

  return (
    <aside className="slip" aria-label="Bet slip" data-layout-ignore>
      <header className="slip-head">
        <h2>Bet slip <span className="slip-count num">{picks.length}</span></h2>
        <button type="button" className="btn btn-ghost btn-sm" onClick={clear}>Clear</button>
        <button type="button" className="btn btn-ghost btn-sm icon-btn" aria-label="Minimise bet slip" onClick={() => setOpen(false)}>
          <X size={16} />
        </button>
      </header>
      <div className="slip-list">
        {picks.map((p) => {
          const stake = stakeOf(p);
          return (
            <div className="slip-item" key={p.key}>
              <div className="slip-item-main">
                <div className="slip-item-title">
                  <span className="slip-selection">{p.selection}</span>
                  <span className="num slip-odds">{odds(p.price)}</span>
                </div>
                <div className="slip-item-sub">
                  {p.matchup} · {MARKET_NAME[p.market]}
                  {p.modelPick && <span className="slip-model">Model pick</span>}
                  {p.priceEstimated && <span title="The book posted the line without a price">Price est. — check your book</span>}
                </div>
              </div>
              <button type="button" className="slip-remove" aria-label={`Remove ${p.selection}`} onClick={() => remove(p.key)}>
                <X size={14} />
              </button>
              <label className="slip-stake">
                <span className="slip-stake-label">Stake</span>
                <span className="slip-stake-input">
                  <span aria-hidden>$</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step={1}
                    value={stakes[p.key] ?? String(stake)}
                    onChange={(e) => setStakes((s) => ({ ...s, [p.key]: e.target.value }))}
                    aria-label={`Stake for ${p.selection}`}
                  />
                </span>
              </label>
              <div className="slip-towin">
                <span>To win</span>
                <strong className="num">{money(toWin(stake, p.price), 2)}</strong>
              </div>
            </div>
          );
        })}
      </div>
      <footer className="slip-foot">
        {error && <div className="slip-error" role="alert">{error}</div>}
        <div className="slip-totals">
          <span>Total stake <strong className="num">{money(total, 2)}</strong></span>
          <span>To win <strong className="num pos">{money(payout, 2)}</strong></span>
        </div>
        <button type="button" className="btn btn-primary slip-place" disabled={busy || !valid} onClick={place}>
          {busy ? 'Adding…' : `Add ${picks.length} ${picks.length === 1 ? 'bet' : 'bets'} to My Bets`}
        </button>
        <p className="slip-note">Tracks the bet in DeltaPicks. Place it at your sportsbook too.</p>
      </footer>
    </aside>
  );
}
